/**
 * Chat message display area.
 *
 * Renders chat messages with role-based styling:
 * - User messages: right-aligned, orange background
 * - Assistant messages: left-aligned, dark background
 * - System messages: centered, dimmed
 *
 * Features:
 * - Auto-scroll to bottom on new messages
 * - Streaming indicator with pulsing animation and cursor
 * - Simple markdown rendering (bold, italic, code, links)
 * - Image URL detection and inline rendering
 * - Timestamp display (toggleable)
 * - History messages rendered dimmed
 */

import { useRef, useEffect, useState, useCallback, type ReactNode } from 'react';
import type { ChatMessage, TtsDebugSentence, ToolCallEntry } from '../types/chat';
import { getApiUrl } from '../utils/environment';
import { STORAGE_KEYS } from '../utils/constants';
import { getApiAuthHeaders, hasAnyAuth } from '../utils/auth';
import styles from './Chat.module.css';

// ── Helpers ──

/** Format a token count for compact display (e.g. 1234 → "1.2k") */
function fmtTokens(n: number): string {
  if (n < 1000) return n.toLocaleString();
  const k = n / 1000;
  return k >= 10 ? `${Math.round(k)}k` : `${Math.round(k * 10) / 10}k`;
}

// ── Props ──

interface ChatProps {
  /** Chat messages to display */
  messages: ChatMessage[];
  /** Whether to show timestamps on messages */
  showTimestamps?: boolean;
  /** Whether to show gateway badges on messages (auto-enabled when >1 gateway) */
  showGatewayBadge?: boolean;
  /** Incrementing counter that forces scroll-to-bottom (e.g. after history loads) */
  scrollToBottomTrigger?: number;
}

// ── Upload URL Rendering ──

/** Pattern matching the S3 bucket used for uploads */
const S3_BUCKET_PATTERN = 'voice-ai-uploads-xxz-dk.s3.amazonaws.com';

const IMAGE_EXT_RE = /\.(jpg|jpeg|png|gif|webp|svg)(\?[^\s]*)?$/i;
const AUDIO_EXT_RE = /\.(mp3|wav|m4a|aac|ogg|oga|flac)(\?[^\s]*)?$/i;
const VIDEO_EXT_RE = /\.(mp4|mov|m4v|webm|ogv)(\?[^\s]*)?$/i;
const FILE_EXT_RE = /\.(pdf|txt|csv|json|md|doc|docx|xls|xlsx|ppt|pptx|zip|gz|tar|rar|7z)(\?[^\s]*)?$/i;

function extractS3Key(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (parsed.hostname === S3_BUCKET_PATTERN) {
      return parsed.pathname.replace(/^\//, '');
    }
  } catch {
    // Ignore invalid URLs, the caller will treat them as plain text.
  }
  return null;
}

function getUrlFilename(url: string): string {
  try {
    const parsed = new URL(url);
    const lastSegment = parsed.pathname.split('/').filter(Boolean).pop();
    return lastSegment ? decodeURIComponent(lastSegment) : url;
  } catch {
    return url;
  }
}

function useResolvedUploadUrl(rawUrl: string) {
  const initialS3Key = extractS3Key(rawUrl);
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(initialS3Key ? null : rawUrl);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const s3Key = extractS3Key(rawUrl);
    setError(false);

    if (!s3Key) {
      setResolvedUrl(rawUrl);
      return;
    }

    const backendUrl = getApiUrl();
    const authHeaders = getApiAuthHeaders(STORAGE_KEYS.UPLOAD_AUTH_TOKEN);

    if (!backendUrl || !hasAnyAuth(STORAGE_KEYS.UPLOAD_AUTH_TOKEN)) {
      setResolvedUrl(rawUrl);
      return;
    }

    const fetchPresigned = async () => {
      try {
        const resp = await fetch(
          `${backendUrl}/api/presign-get?key=${encodeURIComponent(s3Key)}`,
          { headers: authHeaders },
        );
        if (!resp.ok) throw new Error(`presign-get ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) setResolvedUrl(data.url);
      } catch (err) {
        console.error('Failed to fetch presigned URL:', err);
        if (!cancelled) {
          setError(true);
          setResolvedUrl(null);
        }
      }
    };

    setResolvedUrl(null);
    fetchPresigned();
    return () => {
      cancelled = true;
    };
  }, [rawUrl]);

  return { resolvedUrl, error };
}

function UploadLoading({ label }: { label: string }) {
  return (
    <div className={styles.mediaLoading}>
      Loading {label}…
    </div>
  );
}

function UploadUnavailable({ label }: { label: string }) {
  return <span className={styles.mediaUnavailable}>⚠️ {label} unavailable</span>;
}

function UploadImage({ rawUrl, alt }: { rawUrl: string; alt: string }) {
  const { resolvedUrl, error } = useResolvedUploadUrl(rawUrl);

  if (error) return <UploadUnavailable label="image" />;
  if (!resolvedUrl) return <UploadLoading label="image" />;

  return (
    <a href={resolvedUrl} target="_blank" rel="noopener noreferrer">
      <img
        src={resolvedUrl}
        alt={alt}
        className={styles.inlineImage}
        loading="lazy"
      />
    </a>
  );
}

function UploadAudio({ rawUrl }: { rawUrl: string }) {
  const { resolvedUrl, error } = useResolvedUploadUrl(rawUrl);
  const filename = getUrlFilename(rawUrl);

  if (error) return <UploadUnavailable label="audio" />;
  if (!resolvedUrl) return <UploadLoading label="audio" />;

  return (
    <div className={styles.mediaCard}>
      <audio className={styles.inlineAudio} controls preload="metadata" src={resolvedUrl} />
      <a href={resolvedUrl} target="_blank" rel="noopener noreferrer" className={styles.mediaLink}>
        {filename}
      </a>
    </div>
  );
}

function UploadVideo({ rawUrl }: { rawUrl: string }) {
  const { resolvedUrl, error } = useResolvedUploadUrl(rawUrl);
  const filename = getUrlFilename(rawUrl);

  if (error) return <UploadUnavailable label="video" />;
  if (!resolvedUrl) return <UploadLoading label="video" />;

  return (
    <div className={styles.mediaCard}>
      <video className={styles.inlineVideo} controls preload="metadata" src={resolvedUrl} />
      <a href={resolvedUrl} target="_blank" rel="noopener noreferrer" className={styles.mediaLink}>
        {filename}
      </a>
    </div>
  );
}

function UploadFileCard({ rawUrl }: { rawUrl: string }) {
  const { resolvedUrl, error } = useResolvedUploadUrl(rawUrl);
  const filename = getUrlFilename(rawUrl);

  if (error) return <UploadUnavailable label="file" />;
  if (!resolvedUrl) return <UploadLoading label="file" />;

  return (
    <a href={resolvedUrl} target="_blank" rel="noopener noreferrer" className={styles.fileCard}>
      <span className={styles.fileCardIcon}>📎</span>
      <span className={styles.fileCardMeta}>
        <span className={styles.fileCardName}>{filename}</span>
        <span className={styles.fileCardHint}>Open file</span>
      </span>
    </a>
  );
}

function renderUrl(url: string, key: number): ReactNode {
  if (IMAGE_EXT_RE.test(url)) {
    return <UploadImage key={key} rawUrl={url} alt="Shared image" />;
  }
  if (AUDIO_EXT_RE.test(url)) {
    return <UploadAudio key={key} rawUrl={url} />;
  }
  if (VIDEO_EXT_RE.test(url)) {
    return <UploadVideo key={key} rawUrl={url} />;
  }
  if (FILE_EXT_RE.test(url)) {
    return <UploadFileCard key={key} rawUrl={url} />;
  }
  return (
    <a key={key} href={url} target="_blank" rel="noopener noreferrer">
      {url}
    </a>
  );
}

// ── Simple Markdown Rendering ──

/**
 * Split text into segments with simple inline markdown rendering.
 *
 * Supports: **bold**, *italic*, `code`, URLs (auto-linked), upload/media URLs
 * (rendered inline when possible). Does NOT handle block-level markdown,
 * intentional for short chat messages.
 */
function renderMarkdown(text: string): ReactNode[] {
  const TOKEN_RE =
    /(`[^`]+`)|(\*\*[^*]+\*\*)|(?<!\*)\*([^*]+)\*(?!\*)|(https?:\/\/[^\s<>"'()]*(?:\([^\s<>"'()]*\)[^\s<>"'()]*)*)/g;

  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let keyIdx = 0;

  while ((match = TOKEN_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    const fullMatch = match[0];

    if (match[1]) {
      nodes.push(
        <code key={keyIdx++} className={styles.inlineCode}>
          {fullMatch.slice(1, -1)}
        </code>,
      );
    } else if (match[2]) {
      nodes.push(<strong key={keyIdx++}>{fullMatch.slice(2, -2)}</strong>);
    } else if (match[3]) {
      nodes.push(<em key={keyIdx++}>{match[3]}</em>);
    } else if (fullMatch.startsWith('http')) {
      nodes.push(renderUrl(fullMatch, keyIdx++));
    }

    lastIndex = match.index + fullMatch.length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  if (nodes.length === 0) {
    nodes.push(text);
  }

  return nodes;
}

// ── Streaming Cursor ──

function StreamingCursor() {
  return <span className={styles.cursor}>▊</span>;
}

// ── Thinking Indicator ──

function ThinkingIndicator({
  isThinking,
  thinkingDuration,
  thinkingText,
  showStream,
}: {
  isThinking: boolean;
  thinkingDuration?: number;
  thinkingText?: string;
  showStream: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const streamRef = useRef<HTMLDivElement>(null);

  // Auto-scroll the live stream box to the bottom as new text arrives
  useEffect(() => {
    if (isThinking && showStream && streamRef.current) {
      streamRef.current.scrollTop = streamRef.current.scrollHeight;
    }
  }, [isThinking, showStream, thinkingText]);

  if (isThinking) {
    const hasStreamText = showStream && !!thinkingText?.trim();
    return (
      <div className={styles.thinkingIndicator}>
        <div className={styles.thinkingHeader}>
          <span className={styles.thinkingPulse}>💭</span>
          <span className={styles.thinkingLabel}>Thinking…</span>
        </div>
        {hasStreamText && (
          <div ref={streamRef} className={styles.thinkingStreamBox}>
            {thinkingText}
          </div>
        )}
      </div>
    );
  }

  if (thinkingDuration != null) {
    const hasText = !!thinkingText?.trim();
    return (
      <div className={styles.thinkingCollapsed}>
        <span
          className={`${styles.thinkingCollapsedLabel} ${hasText ? styles.thinkingClickable : ''}`}
          onClick={hasText ? () => setExpanded((v) => !v) : undefined}
        >
          💭 Thought for {thinkingDuration}s{hasText ? (expanded ? ' ▾' : ' ▸') : ''}
        </span>
        {expanded && hasText && (
          <div className={styles.thinkingExpandedText}>{thinkingText}</div>
        )}
      </div>
    );
  }

  return null;
}

// ── Compaction Divider ──

function CompactionDivider() {
  return (
    <div className={styles.compactionDivider}>
      <span className={styles.compactionLine} />
      <span className={styles.compactionLabel}>🧹 Context compacted</span>
      <span className={styles.compactionLine} />
    </div>
  );
}

// ── TTS Debug Panel ──

/** Pastel colors that work on dark backgrounds (semi-transparent) */
const TTS_DEBUG_COLORS = [
  'rgba(76, 175, 80, 0.25)',   // green
  'rgba(33, 150, 243, 0.25)',  // blue
  'rgba(255, 152, 0, 0.25)',   // orange
  'rgba(156, 39, 176, 0.25)',  // purple
  'rgba(0, 188, 212, 0.25)',   // cyan
  'rgba(233, 30, 99, 0.25)',   // pink
  'rgba(139, 195, 74, 0.25)',  // light green
  'rgba(255, 87, 34, 0.25)',   // deep orange
];

/** Border colors matching the backgrounds */
const TTS_DEBUG_BORDER_COLORS = [
  'rgba(76, 175, 80, 0.6)',
  'rgba(33, 150, 243, 0.6)',
  'rgba(255, 152, 0, 0.6)',
  'rgba(156, 39, 176, 0.6)',
  'rgba(0, 188, 212, 0.6)',
  'rgba(233, 30, 99, 0.6)',
  'rgba(139, 195, 74, 0.6)',
  'rgba(255, 87, 34, 0.6)',
];

/** Circled number characters for order display */
const CIRCLED_NUMBERS = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩',
  '⑪', '⑫', '⑬', '⑭', '⑮', '⑯', '⑰', '⑱', '⑲', '⑳'];

function TtsDebugPanel({ sentences }: { sentences: TtsDebugSentence[] }) {
  if (sentences.length === 0) return null;

  return (
    <div className={styles.ttsDebugPanel}>
      <div className={styles.ttsDebugHeader}>🔊 TTS Debug — {sentences.length} sentence{sentences.length !== 1 ? 's' : ''} spoken</div>
      {sentences.map((s) => {
        const colorIdx = (s.order - 1) % TTS_DEBUG_COLORS.length;
        const num = s.order <= CIRCLED_NUMBERS.length ? CIRCLED_NUMBERS[s.order - 1] : `(${s.order})`;
        return (
          <div
            key={s.order}
            className={styles.ttsDebugEntry}
            style={{
              background: TTS_DEBUG_COLORS[colorIdx],
              borderLeft: `3px solid ${TTS_DEBUG_BORDER_COLORS[colorIdx]}`,
            }}
          >
            <span className={styles.ttsDebugNum}>{num}</span>
            <span className={styles.ttsDebugText}>"{s.text}"</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Tool Call List ──

function ToolCallList({ toolCalls }: { toolCalls: ToolCallEntry[] }) {
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());

  if (toolCalls.length === 0) return null;

  const toggle = (index: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  return (
    <div className={styles.toolCallList}>
      {toolCalls.map((tc, i) => {
        const isRunning = tc.status === 'running';
        const isOpen = expanded.has(i);
        const canToggle = !isRunning && !!tc.arg;

        return (
          <div
            key={`${tc.name}-${i}`}
            className={[
              styles.toolCallEntry,
              isRunning ? styles.toolCallRunning : styles.toolCallDone,
              canToggle ? styles.toolCallTappable : '',
            ].filter(Boolean).join(' ')}
          >
            <div
              className={styles.toolCallRow}
              onClick={canToggle ? (e) => { e.stopPropagation(); toggle(i); } : undefined}
            >
              <span className={styles.toolCallIcon}>
                {isRunning ? '🔧' : '✅'}
              </span>
              {canToggle && (
                <span className={styles.toolCallChevron}>
                  {isOpen ? '▾' : '▸'}
                </span>
              )}
              <span className={styles.toolCallName}>{tc.name}</span>
              {tc.arg && !isOpen && (
                <>
                  <span className={styles.toolCallSep}>:</span>
                  <span className={styles.toolCallArg}>
                    {tc.arg.length > 80 ? tc.arg.slice(0, 77) + '…' : tc.arg}
                  </span>
                </>
              )}
            </div>
            {isOpen && tc.arg && (
              <div className={styles.toolCallArgExpanded}>{tc.arg}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Inline Content with Interleaved Tool Calls ──

/**
 * Render message content with tool calls interleaved at their content offsets.
 *
 * Tool calls without a contentOffset default to offset 0 (shown at the top).
 * This produces the expected order: text before the tool call, then the tool
 * call card, then text after the tool call.
 */
function InlineContent({
  content,
  toolCalls,
  isStreaming,
  isThinking,
  isNoReply,
}: {
  content: string;
  toolCalls?: ToolCallEntry[];
  isStreaming: boolean;
  isThinking?: boolean;
  isNoReply?: boolean;
}) {
  // Fast path: no tool calls — render content normally in a single .content div
  if (!toolCalls || toolCalls.length === 0) {
    return (
      <div className={styles.content}>
        {isThinking && !content ? null : (
          <>
            {isNoReply ? (
              <span style={{ color: '#666', fontStyle: 'italic', fontSize: '13px' }}>
                No reply needed
              </span>
            ) : (
              renderMarkdown(content)
            )}
            {isStreaming && <StreamingCursor />}
          </>
        )}
      </div>
    );
  }

  // Group tool calls by contentOffset, sorted ascending
  const sorted = [...toolCalls].sort(
    (a, b) => (a.contentOffset ?? 0) - (b.contentOffset ?? 0),
  );

  const groups: { offset: number; calls: ToolCallEntry[] }[] = [];
  for (const tc of sorted) {
    const offset = tc.contentOffset ?? 0;
    const last = groups[groups.length - 1];
    if (last && last.offset === offset) {
      last.calls.push(tc);
    } else {
      groups.push({ offset, calls: [tc] });
    }
  }

  // Build interleaved segments: text slice → tool call group → text slice → …
  const segments: ReactNode[] = [];
  let cursor = 0;
  let keyIdx = 0;

  for (const group of groups) {
    const offset = Math.min(group.offset, content.length);

    // Text segment before this tool call group
    if (offset > cursor) {
      const slice = content.slice(cursor, offset);
      if (slice) {
        segments.push(
          <div key={`t-${keyIdx++}`} className={styles.content}>
            {renderMarkdown(slice)}
          </div>,
        );
      }
    }

    segments.push(
      <ToolCallList key={`tc-${keyIdx++}`} toolCalls={group.calls} />,
    );

    cursor = Math.max(cursor, offset);
  }

  // Remaining text after last tool call group + streaming cursor
  const remaining = content.slice(cursor);

  if (isThinking && !content) {
    // Still thinking, nothing to render
  } else if (isNoReply) {
    segments.push(
      <div key={`t-${keyIdx++}`} className={styles.content}>
        <span style={{ color: '#666', fontStyle: 'italic', fontSize: '13px' }}>
          No reply needed
        </span>
      </div>,
    );
  } else if (remaining || isStreaming) {
    segments.push(
      <div key={`t-${keyIdx++}`} className={styles.content}>
        {remaining ? renderMarkdown(remaining) : null}
        {isStreaming && <StreamingCursor />}
      </div>,
    );
  }

  return <>{segments}</>;
}

// ── Reply Quote Component ──

function ReplyQuote({ text, targetId, onTap }: { text: string; targetId: string; onTap: (id: string) => void }) {
  return (
    <div className={styles.replyQuote} onClick={() => onTap(targetId)}>
      <div className={styles.replyQuoteBar} />
      <div className={styles.replyQuoteText}>{text}</div>
    </div>
  );
}

// ── Message Component ──

function MessageBubble({
  message,
  showTimestamp,
  showGatewayBadge,
  showThinkingStream,
  ttsDebugSentences,
  onReplyTap,
}: {
  message: ChatMessage;
  showTimestamp: boolean;
  showGatewayBadge: boolean;
  showThinkingStream: boolean;
  ttsDebugSentences?: TtsDebugSentence[];
  onReplyTap?: (id: string) => void;
}) {
  const isSubagent = !!message.agentLabel;
  const isPrimaryAssistant = message.role === 'assistant' && !isSubagent;

  const classNames = [
    styles.message,
    styles[message.role],
    isPrimaryAssistant ? styles.primaryAssistant : '',
    isSubagent ? styles.subagentMessage : '',
    message.isHistory ? styles.history : '',
    message.isStreaming ? styles.streaming : '',
    message.isNoReply ? styles.noReply : '',
    message.isDraft ? styles.draftMessage : '',
    message.ttsStatus === 'speaking' ? styles.ttsSpeaking : '',
    message.ttsStatus === 'spoken' ? styles.ttsSpoken : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classNames} data-message-id={message.id}>
      {showGatewayBadge && message.gatewayName && (
        <span className={styles.gatewayBadge}>{message.gatewayName}</span>
      )}
      {message.agentLabel && (
        <span className={styles.agentBadge}>{message.agentLabel}</span>
      )}
      {message.role === 'assistant' && message.replyToText && message.replyToId && onReplyTap && (
        <ReplyQuote text={message.replyToText} targetId={message.replyToId} onTap={onReplyTap} />
      )}
      {message.role === 'assistant' && (message.isThinking || message.thinkingDuration != null) && (
        <ThinkingIndicator
          isThinking={!!message.isThinking}
          thinkingDuration={message.thinkingDuration}
          thinkingText={message.thinkingText}
          showStream={showThinkingStream}
        />
      )}
      <InlineContent
        content={message.content}
        toolCalls={message.toolCalls}
        isStreaming={message.isStreaming}
        isThinking={message.isThinking}
        isNoReply={message.isNoReply}
      />
      {showTimestamp && message.timestamp && (
        <span className={styles.time}>
          {new Date(message.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
          {message.role === 'assistant' && (message.inputTokens || message.outputTokens) && (
            <span className={styles.msgTokens}>
              {' · '}
              {fmtTokens((message.inputTokens ?? 0) + (message.outputTokens ?? 0))} tokens
            </span>
          )}
          {message.ttsStatus === 'speaking' && ' 🔊'}
          {message.ttsStatus === 'spoken' && ' ✓'}
        </span>
      )}
      {ttsDebugSentences && ttsDebugSentences.length > 0 && (
        <TtsDebugPanel sentences={ttsDebugSentences} />
      )}
    </div>
  );
}

// ── Main Chat Component ──

export function Chat({ messages, showTimestamps = true, showGatewayBadge = false, scrollToBottomTrigger }: ChatProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isAutoScrollRef = useRef(true);
  const userInteractingRef = useRef(false);
  const interactionTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Track user-initiated scroll interactions (wheel/touch) to distinguish from
  // programmatic scrolls and content-growth-triggered scroll events.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const markInteracting = () => {
      userInteractingRef.current = true;
      if (interactionTimeoutRef.current) clearTimeout(interactionTimeoutRef.current);
      interactionTimeoutRef.current = setTimeout(() => {
        userInteractingRef.current = false;
      }, 150);
    };

    container.addEventListener('wheel', markInteracting, { passive: true });
    container.addEventListener('touchstart', markInteracting, { passive: true });
    container.addEventListener('touchmove', markInteracting, { passive: true });

    return () => {
      container.removeEventListener('wheel', markInteracting);
      container.removeEventListener('touchstart', markInteracting);
      container.removeEventListener('touchmove', markInteracting);
      if (interactionTimeoutRef.current) clearTimeout(interactionTimeoutRef.current);
    };
  }, []);

  const scrollToMessage = useCallback((id: string) => {
    const el = containerRef.current?.querySelector(`[data-message-id="${id}"]`) as HTMLElement | null;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add(styles.messageHighlight);
    setTimeout(() => {
      el.classList.remove(styles.messageHighlight);
    }, 1500);
  }, []);

  // Check if TTS debug is enabled via localStorage (check once on mount + on storage events)
  const [ttsDebugEnabled, setTtsDebugEnabled] = useState(() =>
    localStorage.getItem(STORAGE_KEYS.TTS_DEBUG) === 'true'
  );
  // Check if thinking stream is enabled (default: true)
  const [showThinkingStream, setShowThinkingStream] = useState(() =>
    localStorage.getItem(STORAGE_KEYS.SHOW_THINKING_STREAM) !== 'false'
  );
  useEffect(() => {
    const onStorage = () => {
      setTtsDebugEnabled(localStorage.getItem(STORAGE_KEYS.TTS_DEBUG) === 'true');
      setShowThinkingStream(localStorage.getItem(STORAGE_KEYS.SHOW_THINKING_STREAM) !== 'false');
    };
    window.addEventListener('storage', onStorage);
    // Also re-check periodically since same-tab localStorage changes don't fire storage events
    const interval = setInterval(onStorage, 2000);
    return () => {
      window.removeEventListener('storage', onStorage);
      clearInterval(interval);
    };
  }, []);

  // Track if user has scrolled up (disable auto-scroll).
  // Only disable auto-scroll when the USER is actively interacting (wheel/touch).
  // Content-growth scroll events (programmatic) must not disable auto-scroll.
  const handleScroll = () => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const atBottom = scrollHeight - scrollTop - clientHeight < 100;

    if (atBottom) {
      // Always re-enable auto-scroll when at bottom
      isAutoScrollRef.current = true;
    } else if (userInteractingRef.current) {
      // Only disable auto-scroll if the USER is actively scrolling
      isAutoScrollRef.current = false;
    }
    // If not at bottom AND not user-initiated → leave unchanged (content grew)
  };

  // Auto-scroll to bottom on new messages (only if user hasn't scrolled up)
  useEffect(() => {
    if (containerRef.current && isAutoScrollRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages]);

  // Force scroll to bottom when history loads (bypasses isAutoScrollRef)
  useEffect(() => {
    if (scrollToBottomTrigger && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
      isAutoScrollRef.current = true;
    }
  }, [scrollToBottomTrigger]);

  return (
    <div
      className={styles.container}
      ref={containerRef}
      onScroll={handleScroll}
    >
      {messages.length === 0 && (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>💬</div>
          <p className={styles.emptyText}>Tap the orb to start talking</p>
        </div>
      )}
      {messages.map((msg) =>
        msg.isCompaction ? (
          <CompactionDivider key={msg.id} />
        ) : (
          <MessageBubble
            key={msg.id}
            message={msg}
            showTimestamp={showTimestamps}
            showGatewayBadge={showGatewayBadge}
            showThinkingStream={showThinkingStream}
            ttsDebugSentences={
              ttsDebugEnabled && msg.role === 'assistant' && !msg.isHistory && msg.ttsDebugSentences?.length
                ? msg.ttsDebugSentences
                : undefined
            }
            onReplyTap={scrollToMessage}
          />
        ),
      )}
    </div>
  );
}
